/**
 * Support chat, practice side: /api/support/practice/{practice_id}/...
 *
 * `practiceId` is always an explicit argument and only ever travels in the URL,
 * where the tenant middleware checks membership and arms RLS for it. The sender
 * (name, user, role) comes from the verified token on the server, so no request
 * body below carries a name, role, user or practice — the backend rejects extra
 * fields with 422 anyway.
 *
 * Goes through the shared axios instance, which has no response interceptor:
 * a 403 (a role the test/live mode does not admit) or 404 (test mode, or a
 * backend without the feature) stays with the caller and triggers no logout,
 * redirect or toast.
 */
import axios from './axiosInstance';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const practiceBase = (practiceId) => `${API_BASE_URL}/support/practice/${practiceId}`;

// Idempotency key for ONE draft. Generate it once, reuse it on every retry of
// that draft, so a send whose response got lost can never post twice.
// `crypto.randomUUID` needs a secure context (https or localhost), like TAB_ID in
// axiosInstance.js; on a plain-http origin the same v4 UUID is built from
// crypto.getRandomValues, which works everywhere.
export const newClientMsgId = () => {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40; // version 4
  b[8] = (b[8] & 0x3f) | 0x80; // RFC 4122 variant
  const hex = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

// {enabled, mode, features}; useSupportStatus reads anything but `enabled: true`
// with mode 'test' or 'live' as off.
export const getStatus = async (practiceId) => {
  const res = await axios.get(`${practiceBase(practiceId)}/status`);
  return res.data;
};

export const listConversations = async (practiceId) => {
  const res = await axios.get(`${practiceBase(practiceId)}/conversations`);
  return Array.isArray(res.data) ? res.data : [];
};

export const getConversation = async (practiceId, conversationId) => {
  const res = await axios.get(`${practiceBase(practiceId)}/conversations/${conversationId}`);
  return res.data;
};

// `attachment_ids` only when there are any, so a text message is sent exactly as
// before and an older backend never sees the key. The ids are uploads of this
// user in this practice (uploadAttachment); the body may then be ''.
const withAttachments = (attachmentIds) => (
  Array.isArray(attachmentIds) && attachmentIds.length ? { attachment_ids: attachmentIds } : {}
);

// Opens a request with its first message. Resolves to {conversation, message}.
export const openConversation = async (practiceId, { body, clientMsgId, taskId = null, attachmentIds = [] }) => {
  const res = await axios.post(`${practiceBase(practiceId)}/conversations`, {
    body,
    client_msg_id: clientMsgId,
    task_id: taskId || null,
    ...withAttachments(attachmentIds),
  });
  return res.data;
};

// Follow-up in an existing request. Resolves to {message}.
export const sendMessage = async (practiceId, conversationId, { body, clientMsgId, attachmentIds = [] }) => {
  const res = await axios.post(`${practiceBase(practiceId)}/conversations/${conversationId}/messages`, {
    body,
    client_msg_id: clientMsgId,
    ...withAttachments(attachmentIds),
  });
  return res.data;
};

// A Blob as the raw request body (not multipart), with its own type and a time limit.
const postBlob = async (url, blob, fallbackType, timeout) => {
  const res = await axios.post(url, blob, { headers: { 'Content-Type': blob.type || fallbackType }, timeout });
  return res.data;
};

// One prepared image (components/support/prepareImage.js). Resolves to
// {attachment}: pending until a message names it, and only its uploader can
// attach it, only in this practice. Without the time limit a stalled upload spins
// on the thumbnail forever while "Senden" stays locked; what takes longer than
// 45 s is broken, not slow.
export const uploadAttachment = (practiceId, blob) => (
  postBlob(`${practiceBase(practiceId)}/attachments`, blob, 'application/octet-stream', 45000)
);

// The bytes of an attached image, as a Blob. An <img src> cannot carry the
// token, so the window shows it through an object URL (SupportProvider caches
// those per practice).
export const fetchAttachment = async (practiceId, attachmentId) => {
  const res = await axios.get(`${practiceBase(practiceId)}/attachments/${attachmentId}`, { responseType: 'blob' });
  return res.data;
};

// A recording (MediaRecorder output) to text. Resolves to {text}. Nothing is
// stored; the text goes into the composer and is never sent by itself.
export const dictate = (practiceId, blob) => postBlob(`${practiceBase(practiceId)}/dictation`, blob, 'audio/webm', 90000);

// Moves the caller's read marker up to that message; the server never moves it
// back. Resolves to {ok: true}.
export const markRead = async (practiceId, conversationId, lastReadMessageId) => {
  const res = await axios.put(`${practiceBase(practiceId)}/conversations/${conversationId}/read`, {
    last_read_message_id: lastReadMessageId,
  });
  return res.data;
};

// Marks a request erledigt (closed: true) or reopens it. Resolves to the conversation.
export const setClosed = async (practiceId, conversationId, closed) => {
  const res = await axios.put(`${practiceBase(practiceId)}/conversations/${conversationId}/closed`, {
    closed: !!closed,
  });
  return res.data;
};
